/**
 * Modern MediaRecorder-based replacement for mic-recorder-to-mp3
 * Provides the same API but uses native browser APIs for better performance and reliability
 */
import React from "react";

export interface AudioRecorderOptions {
  bitRate?: number;
  sampleRate?: number;
  mimeType?: string;
}

export class AudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private audioStream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private isRecording = false;
  private options: AudioRecorderOptions;

  constructor(options: AudioRecorderOptions = {}) {
    this.options = {
      bitRate: options.bitRate || 128,
      sampleRate: options.sampleRate || 44100,
      mimeType: options.mimeType || this.getBestMimeType(),
    };
  }

  private getBestMimeType(): string {
    // Check supported formats in order of preference
    const mimeTypes = [
      "audio/mp4;codecs=mp4a.40.2",
      "audio/mp4",
      "audio/aac",
      "audio/m4a",
      "audio/mpeg",
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/wav",
    ];

    for (const type of mimeTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    // Fallback - this should never happen in modern browsers
    return "audio/webm";
  }

  async start(): Promise<void> {
    if (this.isRecording) {
      throw new Error("Recording is already in progress");
    }

    try {
      // Request microphone access
      this.audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: this.options.sampleRate,
        },
      });

      // Create MediaRecorder with optimal settings
      const mediaRecorderOptions: MediaRecorderOptions = {
        mimeType: this.options.mimeType,
      };

      // Add bitrate if supported (not all browsers support this)
      if (
        "audioBitsPerSecond" in MediaRecorder.prototype &&
        this.options.bitRate
      ) {
        (mediaRecorderOptions as any).audioBitsPerSecond =
          this.options.bitRate * 1000;
      }

      this.mediaRecorder = new MediaRecorder(
        this.audioStream,
        mediaRecorderOptions,
      );
      this.chunks = [];

      // Set up event handlers
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.chunks.push(event.data);
        }
      };

      this.mediaRecorder.onerror = (event) => {
        console.error("MediaRecorder error:", event);
        this.cleanup();
      };

      // Start recording
      this.mediaRecorder.start(100); // Collect data every 100ms
      this.isRecording = true;
    } catch (error) {
      this.cleanup();
      throw new Error(
        `Failed to start recording: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  stop(): void {
    if (!this.isRecording || !this.mediaRecorder) {
      return;
    }

    this.mediaRecorder.stop();
    // DON'T set isRecording = false here! Wait for onstop event

    // Stop all audio tracks
    if (this.audioStream) {
      this.audioStream.getTracks().forEach((track) => track.stop());
    }
  }

  async getMp3(): Promise<[ArrayBuffer, Blob]> {
    return new Promise((resolve, reject) => {
      // If still recording, stop first
      if (this.isRecording && this.mediaRecorder) {
        this.mediaRecorder.stop();

        // Stop all audio tracks
        if (this.audioStream) {
          this.audioStream.getTracks().forEach((track) => track.stop());
        }
      }

      if (!this.mediaRecorder) {
        reject(new Error("No recording available"));
        return;
      }

      // Wait for the stop event to ensure all data is collected
      this.mediaRecorder.onstop = async () => {
        try {
          this.isRecording = false; // Set this only after onstop event

          if (this.chunks.length === 0) {
            reject(new Error("No audio data recorded"));
            return;
          }

          // Create blob from chunks
          const blob = new Blob(this.chunks, { type: this.options.mimeType });

          // Convert blob to ArrayBuffer for backward compatibility
          const arrayBuffer = await blob.arrayBuffer();

          this.cleanup();
          resolve([arrayBuffer, blob]);
        } catch (error) {
          reject(
            new Error(
              `Failed to process recording: ${error instanceof Error ? error.message : "Unknown error"}`,
            ),
          );
        }
      };

      // If recorder is already stopped, trigger the stop event manually
      if (
        this.mediaRecorder.state === "inactive" &&
        this.mediaRecorder.onstop
      ) {
        this.mediaRecorder.onstop(new Event("stop") as any);
      }
    });
  }

  private cleanup(): void {
    if (this.audioStream) {
      this.audioStream.getTracks().forEach((track) => track.stop());
      this.audioStream = null;
    }

    this.mediaRecorder = null;
    this.chunks = [];
    this.isRecording = false;
  }

  // Additional utility methods for better control
  get state(): string {
    return this.mediaRecorder?.state || "inactive";
  }

  get isActive(): boolean {
    return this.isRecording;
  }

  // Clean up resources when done
  dispose(): void {
    if (this.isRecording) {
      this.stop();
    }
    this.cleanup();
  }
}

// Hook for React components
export const useAudioRecorder = (options: AudioRecorderOptions = {}) => {
  const [recorder, setRecorder] = React.useState<AudioRecorder | null>(null);
  const [isRecording, setIsRecording] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Create recorder instance
  const createRecorder = React.useCallback(() => {
    const newRecorder = new AudioRecorder(options);
    setRecorder(newRecorder);
    setError(null);
    return newRecorder;
  }, [options]);

  // Start recording
  const startRecording = React.useCallback(async () => {
    try {
      setError(null);
      const currentRecorder = recorder || createRecorder();
      await currentRecorder.start();
      setIsRecording(true);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to start recording";
      setError(errorMessage);
      throw err;
    }
  }, [recorder, createRecorder]);

  // Stop recording
  const stopRecording = React.useCallback(() => {
    if (recorder && isRecording) {
      recorder.stop();
      setIsRecording(false);
    }
  }, [recorder, isRecording]);

  // Get recording data
  const getRecordingData = React.useCallback(async () => {
    if (!recorder) {
      throw new Error("No recorder available");
    }
    return await recorder.getMp3();
  }, [recorder]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (recorder) {
        recorder.dispose();
      }
    };
  }, [recorder]);

  return {
    recorder,
    isRecording,
    error,
    startRecording,
    stopRecording,
    getRecordingData,
    createRecorder,
  };
};

// For direct usage (drop-in replacement for MicRecorder)
export default AudioRecorder;
